import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { allReviews, getPlatformColor } from '../components/ReviewExcerpts';
import { StarIcon, MapPinIcon, FilterIcon } from 'lucide-react';
const LOCATIONS = ['All', 'Mitchellville', 'Clinton', 'Hyattsville', 'Greenbelt', 'Silver Spring', 'Frederick'];
const PLATFORMS = ['All', 'Google', 'Facebook', 'Yelp'];
export function ReviewsPage() {
  const [selectedLocation, setSelectedLocation] = useState('All');
  const [selectedPlatform, setSelectedPlatform] = useState('All');
  // Filter reviews based on selections
  const filteredReviews = useMemo(() => {
    return allReviews.filter(review => {
      const locationMatch = selectedLocation === 'All' || review.location === selectedLocation;
      const platformMatch = selectedPlatform === 'All' || review.platform === selectedPlatform;
      return locationMatch && platformMatch;
    });
  }, [selectedLocation, selectedPlatform]);
  return <>
      <PageHeader title="Patient Reviews" backgroundImage="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1920&q=80" data-id="element-1056" />

      <main className="py-16 md:py-24 bg-surface-muted min-h-screen" data-id="element-1057">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1058">
          {/* Filters Section */}
          <div className="bg-white rounded-card shadow-sm border border-gray-100 p-6 mb-12" data-id="element-1059">
            <div className="flex items-center gap-2 mb-6" data-id="element-1060">
              <FilterIcon className="w-5 h-5 text-brand-gold" data-id="element-1061" />
              <h2 className="text-lg font-heading font-bold text-brand-dark" data-id="element-1062">
                Filter Reviews
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1063">
              {/* Location Filter */}
              <div data-id="element-1064">
                <label className="block text-sm font-semibold text-neutral-text/80 mb-3" data-id="element-1065">
                  By Location
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-1066">
                  {LOCATIONS.map(loc => <button key={loc} onClick={() => setSelectedLocation(loc)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors focus-ring ${selectedLocation === loc ? 'bg-brand-dark text-white' : 'bg-gray-100 text-neutral-text/70 hover:bg-gray-200'}`} data-id="element-1067">
                      {loc}
                    </button>)}
                </div>
              </div>

              {/* Platform Filter */}
              <div data-id="element-1068">
                <label className="block text-sm font-semibold text-neutral-text/80 mb-3" data-id="element-1069">
                  By Platform
                </label>
                <div className="flex flex-wrap gap-2" data-id="element-1070">
                  {PLATFORMS.map(plat => <button key={plat} onClick={() => setSelectedPlatform(plat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors focus-ring ${selectedPlatform === plat ? 'bg-brand-dark text-white' : 'bg-gray-100 text-neutral-text/70 hover:bg-gray-200'}`} data-id="element-1071">
                      {plat}
                    </button>)}
                </div>
              </div>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mb-8" data-id="element-1072">
            <p className="text-neutral-text/70 font-medium" data-id="element-1073">
              Showing{' '}
              <span className="text-brand-dark font-bold" data-id="element-1074">
                {filteredReviews.length}
              </span>{' '}
              review{filteredReviews.length !== 1 ? 's' : ''}
              {selectedLocation !== 'All' && <span data-id="element-1075">
                  {' '}
                  for{' '}
                  <span className="text-brand-dark font-bold" data-id="element-1076">
                    {selectedLocation}
                  </span>
                </span>}
              {selectedPlatform !== 'All' && <span data-id="element-1077">
                  {' '}
                  on{' '}
                  <span className="text-brand-dark font-bold" data-id="element-1078">
                    {selectedPlatform}
                  </span>
                </span>}
            </p>
          </div>

          {/* Reviews Grid */}
          {filteredReviews.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1079">
              <AnimatePresence mode="popLayout" data-id="element-1080">
                {filteredReviews.map(review => <motion.div layout initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} transition={{
              duration: 0.2
            }} key={review.id} className="bg-white p-6 rounded-card shadow-card flex flex-col h-full relative hover:shadow-card-hover transition-shadow" data-id="element-1081">
                    {/* Header: Rating & Platform */}
                    <div className="flex justify-between items-start mb-4" data-id="element-1082">
                      <div className="flex gap-1" data-id="element-1083">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-brand-gold text-brand-gold' : 'text-gray-200'}`} data-id="element-1084" />)}
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${getPlatformColor(review.platform)}`} data-id="element-1085">
                        {review.platform}
                      </span>
                    </div>

                    {/* Content */}
                    <p className="text-neutral-text/80 italic mb-6 text-base leading-relaxed flex-grow" data-id="element-1086">
                      "{review.content}"
                    </p>

                    {/* Footer: Author, Date, Location */}
                    <div className="mt-auto pt-4 border-t border-gray-100" data-id="element-1087">
                      <div className="flex justify-between items-end mb-2" data-id="element-1088">
                        <span className="font-heading font-semibold text-brand-dark text-base" data-id="element-1089">
                          {review.author}
                        </span>
                        <span className="text-sm text-neutral-text/50" data-id="element-1090">
                          {review.date}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-neutral-text/60" data-id="element-1091">
                        <MapPinIcon className="w-3 h-3 text-brand-gold" data-id="element-1092" />
                        {review.location}
                      </div>
                    </div>
                  </motion.div>)}
              </AnimatePresence>
            </div> /* Empty State */ : <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="text-center py-20 bg-white rounded-card shadow-sm border border-gray-100" data-id="element-1093">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1094">
                <FilterIcon className="w-8 h-8 text-gray-400" data-id="element-1095" />
              </div>
              <h3 className="text-xl font-heading font-bold text-brand-dark mb-2" data-id="element-1096">
                No reviews found
              </h3>
              <p className="text-neutral-text/60 mb-6" data-id="element-1097">
                We couldn't find any reviews matching your current filters.
              </p>
              <button onClick={() => {
            setSelectedLocation('All');
            setSelectedPlatform('All');
          }} className="text-brand-gold font-semibold hover:text-brand-gold-light transition-colors" data-id="element-1098">
                Clear all filters
              </button>
            </motion.div>}
        </div>
      </main>
    </>;
}